import {
  Typography,
  Stepper,
  Card,
  LinkButton,
  ShowMore,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { useBlurbContext } from '../../context/Blurb.context';
import { RemovedItem } from '../../hooks/cartHook';
import { testId } from '../../utils';
import { useProductDetails } from '../../utils/project/product';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';

type CartCardProps = {
  itemIndex: number;
  item: Cornerstone.CheckoutLineItem;
  handleChange: (id: number, newQuantity: number) => void;
  removeItem: (item: RemovedItem) => void;
};

export const CartCard = ({
  item,
  itemIndex,
  handleChange,
  removeItem,
}: CartCardProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.cart' });
  const { currencyFormatter } = useBlurbContext();

  const sku = item.sku;
  const { productDetailsStatus, details } = useProductDetails(sku);
  if (item.pageCount) {
    details.push([t('page_count'), item.pageCount.toString()]);
  }

  const ItemDetails = () => {
    return (
      <ul className="list-none">
        {details.map((value, key) => (
          <li key={key}>
            <Typography variant="body-semibold">{value[0]}: </Typography>
            <Typography variant="body">{value[1]}</Typography>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Card {...testId('cart')} key={item.cartLineItemId}>
      <div
        {...testId('cart', 'card', `${item.projectId ?? 'unknown-id'}`)}
        className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-8 p-6 md:p-8"
      >
        <img
          className="max-w-[140px] max-h-[140px] object-contain"
          src={item.thumbnailLargeUrl ?? ''}
          alt={item.title}
        />
        <div
          className="flex-col space-y-2 md:space-y-0 flex-grow"
          data-testid="cart-item-details"
        >
          <div className="flex gap-2">
            <Typography variant="body-semibold">{t('title')}:</Typography>
            <Typography variant="body">{item.title}</Typography>
          </div>
          <div className="flex  gap-2">
            <Typography variant="body-semibold">{t('author')}:</Typography>
            <Typography variant="body">{item.author}</Typography>
          </div>
          <div className="flex gap-2">
            <Typography variant="body-semibold">
              {t('project_type')}:
            </Typography>
            <Typography variant="body">{item.projectType}</Typography>
          </div>
          {productDetailsStatus === 'error' && (
            <Alert variant="error" data-testid="product-rules-error">
              {t('show_details_error')}
            </Alert>
          )}
          <div className="flex">
            <ShowMore
              title={t('show_details')}
              content={<ItemDetails />}
              data-testid="show-details-accordion"
            />
          </div>
        </div>

        <div className="flex flex-col text-center space-y-4 items-center">
          <Stepper
            initialValue={item.quantity}
            onChange={(newQuantity: number) =>
              item.cartLineItemId &&
              handleChange(item.cartLineItemId, newQuantity)
            }
          />
          <LinkButton
            data-testid="cart-remove"
            onClick={() =>
              item.cartLineItemId &&
              item.projectId &&
              removeItem({
                itemIndex: itemIndex,
                itemId: item.cartLineItemId,
                projectId: item.projectId,
                quantity: item.quantity,
                sku: item.sku,
                title: item.title || t('unknown_item'),
              })
            }
          >
            {t('remove')}
          </LinkButton>
        </div>
        <div className="flex flex-col text-right">
          {item.lineItemDiscount && item.lineItemDiscount.amount > 0 ? (
            <>
              <div>
                <Typography variant="body" className="line-through">
                  {currencyFormatter(item.lineItemPrice)}
                </Typography>
              </div>
              <div>
                <Typography variant="body-bold" className="text-green-dark">
                  {currencyFormatter({
                    amount:
                      item.lineItemPrice.amount - item.lineItemDiscount.amount,
                    currency: item.lineItemPrice.currency,
                  })}
                </Typography>
              </div>
            </>
          ) : (
            <Typography variant="body-bold">
              {currencyFormatter(item.lineItemPrice)}
            </Typography>
          )}
        </div>
      </div>
    </Card>
  );
};
